const COMBINING_MARKS = /[\u0300-\u036f]/g;

export function normalizeSmartSearchText(value: unknown): string {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(COMBINING_MARKS, '')
    .toLocaleLowerCase('it-IT')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Human labels are case/diacritic insensitive; never use for secrets or opaque IDs. */
export function sameHumanText(left: unknown, right: unknown): boolean {
  const normalized = normalizeSmartSearchText(left);
  return Boolean(normalized) && normalized === normalizeSmartSearchText(right);
}

export function uniqueHumanLabels(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  return values.flatMap((value) => {
    const label = value?.trim();
    const key = normalizeSmartSearchText(label);
    if (!label || !key || seen.has(key)) return [];
    seen.add(key);
    return [label];
  });
}

export function smartSearchTokens(value: unknown): string[] {
  return [...new Set(normalizeSmartSearchText(value).split(' ').filter(Boolean))].slice(0, 12);
}

function trigrams(value: string): string[] {
  const padded = `  ${value} `;
  return Array.from({ length: Math.max(0, padded.length - 2) }, (_, index) => padded.slice(index, index + 3));
}

export function trigramSimilarity(left: string, right: string): number {
  const leftTrigrams = trigrams(left);
  const remaining = trigrams(right);
  let matches = 0;
  for (const trigram of leftTrigrams) {
    const index = remaining.indexOf(trigram);
    if (index >= 0) {
      matches += 1;
      remaining.splice(index, 1);
    }
  }
  const total = leftTrigrams.length + trigrams(right).length;
  return total === 0 ? 1 : (2 * matches) / total;
}

export function smartSearchThreshold(token: string): number {
  if (token.length >= 9) return 0.42;
  if (token.length >= 6) return 0.5;
  return 0.62;
}

export function smartTextMatches(text: unknown, query: unknown): boolean {
  const normalizedText = normalizeSmartSearchText(text);
  const words = normalizedText.split(/[^\p{L}\p{N}@._+-]+/u).filter(Boolean);
  return smartSearchTokens(query).every((token) => {
    if (normalizedText.includes(token)) return true;
    if (token.length < 4) return false;
    return words.some((word) => (
      word.slice(0, 2) === token.slice(0, 2)
      // A short complete word is not evidence for a longer query (Roma →
      // Romana). Literal matches and partial queries toward longer words stay.
      && (word.length > 4 || word.length >= token.length)
      && (
        (
          Math.abs(word.length - token.length) <= Math.max(2, Math.ceil(token.length * 0.3))
          && trigramSimilarity(token, word) >= smartSearchThreshold(token)
        )
        // Nelle autocomplete l'utente cerca mentre sta ancora scrivendo. Un
        // token parziale con una piccola omissione interna ("ferracc" per
        // "Ferraraccio") deve quindi essere confrontato anche con la sola
        // finestra iniziale della parola, non con tutta la coda non digitata.
        || (
          word.length > token.length
          && trigramSimilarity(token, word.slice(0, token.length + 2)) >= smartSearchThreshold(token)
        )
      )
    ));
  });
}
